import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faRotate } from '@fortawesome/free-solid-svg-icons';
import ActionIconButton from '../../components/ActionIconButton';
import { getClientQueries, updateClientQueryStatus } from '../../service/apiService';

const emptyEdit = { id: 0, status: 'open', admin_response: '' };

function statusVariant(status) {
  return {
    open: 'primary',
    in_progress: 'warning',
    resolved: 'success',
    closed: 'secondary',
  }[status] || 'secondary';
}

function ClientQueries() {
  const [queries, setQueries] = useState([]);
  const [filters, setFilters] = useState({ search: '', status: '' });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [editing, setEditing] = useState(null);

  const loadQueries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getClientQueries(filters);
      if (res.status === 'success') setQueries(res.data?.queries || []);
      else setMessage(res.message || 'Unable to load client queries');
    } catch (error) {
      console.error('Client query inbox error:', error);
      setMessage('Unable to load client queries');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadQueries();
  }, [loadQueries]);

  const openEdit = (query) => {
    setEditing({
      id: query.id,
      subject: query.subject,
      client_name: query.client_name,
      status: query.status || 'open',
      admin_response: query.admin_response || '',
    });
  };

  const saveStatus = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage('');
    try {
      const res = await updateClientQueryStatus(editing || emptyEdit);
      if (res.status === 'success') {
        setEditing(null);
        setMessage('Client query updated');
        await loadQueries();
      } else {
        setMessage(res.message || 'Unable to update query');
      }
    } catch (error) {
      console.error('Client query update error:', error);
      setMessage('Unable to update query');
    } finally {
      setSaving(false);
    }
  };

  const counts = queries.reduce((acc, query) => {
    acc[query.status] = (acc[query.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="client-queries-page">
      <div className="page-title-row">
        <div>
          <span>CRM & CRE</span>
          <h3>Client Queries</h3>
          <p className="text-muted mb-0">Receive, review, and update project questions raised from the client portal.</p>
        </div>
        <Button variant="outline-secondary" onClick={loadQueries}>
          <FontAwesomeIcon icon={faRotate} />
        </Button>
      </div>

      {message && <Alert variant="info" onClose={() => setMessage('')} dismissible>{message}</Alert>}

      <Row className="g-3 mb-3">
        {[
          ['Open', counts.open || 0],
          ['In Progress', counts.in_progress || 0],
          ['Resolved', counts.resolved || 0],
          ['Closed', counts.closed || 0],
        ].map(([label, value]) => (
          <Col md={3} sm={6} key={label}>
            <Card className="erp-panel-card">
              <Card.Body>
                <span className="text-muted small">{label}</span>
                <h4 className="mb-0">{value}</h4>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Card className="erp-panel-card">
        <Card.Body>
          <Row className="g-2 mb-3">
            <Col md={6}><Form.Control placeholder="Search client, project, subject..." value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} /></Col>
            <Col md={3}>
              <Form.Select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
                <option value="">All Status</option>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </Form.Select>
            </Col>
          </Row>

          {loading ? <div className="table-loader"><Spinner animation="border" /></div> : (
            <Table responsive hover className="erp-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Project</th>
                  <th>Query</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Response</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {queries.map((query) => (
                  <tr key={query.id}>
                    <td>
                      <strong>{query.client_name}</strong>
                      <div className="text-muted small">{query.phone || query.email || '-'}</div>
                    </td>
                    <td>{query.project_name || 'General'}</td>
                    <td>
                      <strong>{query.subject}</strong>
                      <div className="text-muted small">{query.message}</div>
                    </td>
                    <td><Badge bg={query.priority === 'urgent' ? 'danger' : query.priority === 'high' ? 'warning' : 'secondary'}>{query.priority}</Badge></td>
                    <td><Badge bg={statusVariant(query.status)}>{String(query.status || '').replace('_', ' ')}</Badge></td>
                    <td>{query.admin_response || '-'}</td>
                    <td>
                      <ActionIconButton size="sm" variant="outline-primary" icon={faPenToSquare} label="Update query" onClick={() => openEdit(query)} />
                    </td>
                  </tr>
                ))}
                {!queries.length && <tr><td colSpan="7" className="text-center text-muted">No client queries found</td></tr>}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={!!editing} onHide={() => setEditing(null)} centered>
        <Form onSubmit={saveStatus}>
          <Modal.Header closeButton>
            <Modal.Title>Update Client Query</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-3">
              <div className="text-muted small">Client</div>
              <strong>{editing?.client_name}</strong>
              <div>{editing?.subject}</div>
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select value={editing?.status || 'open'} onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </Form.Select>
            </Form.Group>
            <Form.Group>
              <Form.Label>Response</Form.Label>
              <Form.Control as="textarea" rows={5} value={editing?.admin_response || ''} onChange={(e) => setEditing({ ...editing, admin_response: e.target.value })} />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setEditing(null)}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save Status'}</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}

export default ClientQueries;
